import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MuiLink from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Description from '@mui/icons-material/esm/Description';
import Architecture from '@mui/icons-material/esm/Architecture';
import Verified from '@mui/icons-material/esm/Verified';
import WorkOutline from '@mui/icons-material/esm/WorkOutline';
import Warning from '@mui/icons-material/esm/Warning';
import { useSelector } from 'react-redux';

import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import documentService, {
  DocumentRegister,
  DocumentRegisterRow,
  DocumentTypeFilter,
  DocumentStatusFilter,
} from '../../services/api/documentService';

const TYPE_LABEL: Record<string, string> = {
  photo: 'Photo',
  delivery_note: 'Delivery note',
  certificate: 'Certificate',
  insurance: 'Insurance',
  permit: 'Permit',
  contract: 'Contract',
  invoice: 'Invoice',
  drawing: 'Drawing',
  report: 'Report',
  drawdown_packet: 'Drawdown packet',
  other: 'Other',
};

const STATUS_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  submitted: 'info',
  approved: 'success',
  rejected: 'error',
  expired: 'warning',
  replaced: 'default',
};

export default function DocumentsPage() {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DocumentRegister | null>(null);
  const [typeFilter, setTypeFilter] = useState<DocumentTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<DocumentStatusFilter>('all');
  const [currentOnly, setCurrentOnly] = useState<boolean>(true);

  useEffect(() => {
    if (!activeProjectId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await documentService.getRegister(activeProjectId, {
          documentType: typeFilter,
          status: statusFilter,
          currentOnly,
        });
        if (!cancelled) setData(result);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load document register');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeProjectId, typeFilter, statusFilter, currentOnly]);

  const rows = useMemo(() => data?.documents ?? [], [data]);

  if (!activeProjectId) {
    return (
      <Box>
        <PageHeader title="Documents / Project Controls" />
        <Alert severity="info">Select a project to view documents.</Alert>
      </Box>
    );
  }

  const columns: Column<DocumentRegisterRow>[] = [
    { id: 'title', label: 'Title', minWidth: 220 },
    {
      id: 'documentType',
      label: 'Type',
      minWidth: 130,
      format: (value: string) => TYPE_LABEL[value] ?? value,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      format: (value: string) => (
        <Chip size="small" label={value} color={STATUS_COLOR[value] || 'default'} />
      ),
    },
    {
      id: 'version',
      label: 'Ver',
      minWidth: 60,
      align: 'right',
    },
    {
      id: 'isCurrent',
      label: 'Current',
      minWidth: 80,
      format: (value: boolean) => (value ? <Chip size="small" label="Current" color="success" /> : <Chip size="small" label="Superseded" color="default" />),
    },
    {
      id: 'drawingNumber',
      label: 'Drawing #',
      minWidth: 110,
      format: (value: string | null) => value ?? '—',
    },
    {
      id: 'discipline',
      label: 'Discipline',
      minWidth: 100,
      format: (value: string | null) => value ?? '—',
    },
    {
      id: 'revisionCode',
      label: 'Rev',
      minWidth: 70,
      format: (value: string | null) => value ?? '—',
    },
    {
      id: 'taskCode',
      label: 'Task',
      minWidth: 100,
      format: (value: string | null) => value ?? '—',
    },
    {
      id: 'uploadedByName',
      label: 'Uploaded by',
      minWidth: 130,
      format: (value: string | null) => value ?? '—',
    },
    {
      id: 'createdAt',
      label: 'Uploaded',
      minWidth: 130,
      format: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'fileUrl',
      label: 'File',
      minWidth: 80,
      format: (value: string, row: DocumentRegisterRow) => (
        <MuiLink href={value} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
          {row.fileName.length > 24 ? row.fileName.slice(0, 22) + '…' : row.fileName}
        </MuiLink>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Documents / Project Controls"
        subtitle={data ? `${data.totals.count} document${data.totals.count === 1 ? '' : 's'} (filtered view)` : undefined}
      />

      {/* Summary KPIs */}
      {data && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard title="Total Documents" value={`${data.totals.count}`} icon={<Description />} color="primary" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard title="Active Drawings" value={`${data.totals.activeDrawings}`} icon={<Architecture />} color="primary" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              title="Approved"
              value={`${data.totals.byStatus.approved ?? 0}`}
              icon={<Verified />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              title="Drawing Conflicts"
              value={`${data.totals.drawingConflicts}`}
              changeLabel={data.totals.drawingConflicts === 0 ? 'No active tasks on stale drawings' : 'Active tasks on superseded/replaced drawings'}
              icon={<Warning />}
              color={data.totals.drawingConflicts > 0 ? 'error' : 'success'}
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value as DocumentTypeFilter)}>
            <MenuItem value="all">All types</MenuItem>
            {Object.entries(TYPE_LABEL).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value as DocumentStatusFilter)}>
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="replaced">Replaced</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={currentOnly} onChange={(e) => setCurrentOnly(e.target.checked)} />}
          label="Current revisions only"
        />
      </Box>

      {/* Drawing conflicts banner */}
      {data && data.drawingConflicts.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {data.drawingConflicts.length} active task{data.drawingConflicts.length === 1 ? ' is' : 's are'} linked to a superseded or replaced drawing.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {data.drawingConflicts.slice(0, 5).map((c) => (
              <li key={`${c.taskId}-${c.drawingId}`}>
                <strong>{c.taskCode}</strong> — {c.taskName} → drawing{' '}
                <strong>{c.drawingNumber || c.drawingTitle}</strong> ({c.drawingStatus})
              </li>
            ))}
            {data.drawingConflicts.length > 5 && (
              <li>… and {data.drawingConflicts.length - 5} more</li>
            )}
          </Box>
        </Alert>
      )}

      {/* Body */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">No documents match the current filters.</Alert>
      ) : (
        <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <DataTable columns={columns} data={rows} pagination searchable />
        </Box>
      )}

      {/* Type breakdown */}
      {data && (
        <Box sx={{ mt: 3, p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>By type</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(data.totals.byType).map(([type, count]) => (
              <Chip
                key={type}
                icon={<WorkOutline />}
                label={`${TYPE_LABEL[type] ?? type}: ${count}`}
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
